'use client'

import { useAppSelector, useSearchParams } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import React, { useMemo } from 'react'

import { ProductCard } from '@/components/molecules/product-card'

import { NoProductsPlaceholder } from '../placeholders/no-products'
import { ProductCardHorizontal } from './product-card-horizontal'

interface ProductsGridProps extends React.ComponentPropsWithoutRef<'div'> {}

// Helper function to generate product key - moved outside component for better performance
const generateProductKey = (product: any) =>
  `${product?.id}-${product?.title}-${product?.default_price?.barcode}-${product?.default_price?.price_id}`

export const ProductsGrid = React.memo<ProductsGridProps>(
  ({ className, ...restProps }) => {
    const { productFilterId } = useSearchParams()
    const { productViewMode } = useAppSelector((state) => state.appSession)

    const { data: products, isPending, isError } = useQuery({
      queryKey: ['products', productFilterId ?? 'all'],
      queryFn: async () => {
        const params = new URLSearchParams()
        if (productFilterId) {
          params.set('categoryId', productFilterId)
        }
        
        const response = await fetch(`/api/products/get-products?${params.toString()}`)
        
        if (!response.ok) {
          throw new Error('Failed to fetch products')
        }
        
        const result = await response.json()
        return result.data || []
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: 2, // Retry failed requests up to 2 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      refetchOnWindowFocus: false, // Prevent refetch on window focus to reduce unnecessary requests
      enabled: true, // Always enabled, but can be controlled if needed
    })

    const gridClassName = useMemo(
      () =>
        cn(
          'grid overflow-auto',
          {
            'gap-5 @sm/products:grid-cols-2 @xl/products:grid-cols-3 @4xl/products:grid-cols-4 @5xl/products:grid-cols-5':
              productViewMode === 'vertical',
            'gap-3 @sm/products:grid-cols-1 @3xl/products:grid-cols-2':
              productViewMode === 'horizontal',
          },
          className,
        ),
      [productViewMode, className],
    )

    const renderedProducts = useMemo(() => {
      return (products || [])?.map((product: any) => {
        const key = generateProductKey(product)

        return productViewMode === 'vertical' ? (
          <ProductCard key={key} product={product} />
        ) : (
          <ProductCardHorizontal key={key} product={product} />
        )
      })
    }, [products, productViewMode])

    return (
      <div
        className={cn(gridClassName, {
          'flex h-full w-full flex-col items-center justify-center':
            (!isPending && !products?.length) || isError,
        })}
        {...restProps}
      >
        {isPending && <div>Loading...</div>}

        {isError && (
          <div className="text-center">
            <p className="text-destructive">Failed to load products</p>
            <p className="text-muted-foreground text-sm">
              Please try again or refresh the page
            </p>
          </div>
        )}

        {!isPending && !isError && !products?.length && <NoProductsPlaceholder />}

        {!isPending && !isError && renderedProducts}
      </div>
    )
  },
)

ProductsGrid.displayName = 'ProductsGrid'
